import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { WorkOrderTimesheetRow } from "../api/types";
import { roleLabel } from "../lib/admin";
import { formatDateTime, formatWoId } from "../lib/workorders";

const COLUMNS = ["Name", "Email", "Role", "Buildings", "Status"];

export function AdminPage() {
  const listQ = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => api.admin.list(),
  });
  const timesheetsQ = useQuery({
    queryKey: ["admin", "work-order-timesheets"],
    queryFn: () => api.admin.timesheets(),
  });

  const users = listQ.data?.users ?? [];
  const entries = timesheetsQ.data?.entries ?? [];

  return (
    <Layout title="Admin — People">
      <p className="help-line">
        Manage who can use the system. Maintenance users only ever see their own assigned work
        orders.
      </p>
      <div className="page-actions">
        <Link to="/admin/new" className="btn btn-primary btn-xl">
          + Add a new user
        </Link>
      </div>

      <section className="card">
        {listQ.isLoading ? (
          <p className="sub">Loading…</p>
        ) : listQ.isError ? (
          <p className="sub">Could not load users. Please try again.</p>
        ) : (
          <div className="table-wrap stack-md">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td data-label="Name">
                      <Link to={`/admin/${u.id}`} className="wo-title-link">
                        {u.fullName}
                      </Link>
                    </td>
                    <td data-label="Email">{u.email}</td>
                    <td data-label="Role">{roleLabel(u.role)}</td>
                    <td data-label="Buildings">
                      {u.role === "maintenance"
                        ? "—"
                        : u.buildings.length
                          ? u.buildings.map((b) => b.name).join(", ")
                          : "None"}
                    </td>
                    <td data-label="Status">
                      <span className={"badge badge-" + (u.status === "active" ? "ok" : "muted")}>
                        {u.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="section-head">
          <h2>Work order timesheets</h2>
          <div className="section-actions">
            <button
              className="btn btn-ghost btn-xl"
              onClick={() => exportTimesheetsExcel(entries)}
              disabled={entries.length === 0}
            >
              Export Excel
            </button>
            <button
              className="btn btn-ghost btn-xl"
              onClick={() => exportTimesheetsPdf(entries)}
              disabled={entries.length === 0}
            >
              Export PDF
            </button>
          </div>
        </div>
        {timesheetsQ.isLoading ? (
          <p className="sub">Loading...</p>
        ) : entries.length === 0 ? (
          <p className="sub">No timer entries recorded yet.</p>
        ) : (
          <div className="table-wrap stack-md">
            <table>
              <thead>
                <tr>
                  <th>Staff/admin</th>
                  <th>Work order</th>
                  <th>Building</th>
                  <th>Started</th>
                  <th>Stopped</th>
                  <th>Minutes</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td data-label="Staff/admin">{e.userName}</td>
                    <td data-label="Work order">
                      {formatWoId(e.workOrderId)} - {e.workOrderTitle}
                    </td>
                    <td data-label="Building">{e.buildingName}</td>
                    <td data-label="Started">{formatDateTime(e.startedAt)}</td>
                    <td data-label="Stopped">{formatDateTime(e.stoppedAt)}</td>
                    <td data-label="Minutes">{e.minutes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}

function exportTimesheetsExcel(entries: WorkOrderTimesheetRow[]) {
  const rows = entries.map((e) => ({
    "Staff/admin": e.userName,
    "Work order ID": formatWoId(e.workOrderId),
    "Work order": e.workOrderTitle,
    Building: e.buildingName,
    Started: formatDateTime(e.startedAt),
    Stopped: formatDateTime(e.stoppedAt),
    Minutes: e.minutes,
    Hours: (e.minutes / 60).toFixed(2),
  }));
  const headers = Object.keys(rows[0] ?? {});
  const totalMinutes = entries.reduce((sum, e) => sum + e.minutes, 0);
  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    table { border-collapse: collapse; font-family: Arial, sans-serif; }
    th { background: #f0f4f8; font-weight: bold; }
    th, td { border: 1px solid #b8c4cf; padding: 6px 8px; }
  </style>
</head>
<body>
  <h1>Royal Heights Work Order Timesheets</h1>
  <p>Generated ${escapeHtml(new Date().toLocaleString("en-US"))}</p>
  <p>Total entries: ${entries.length} | Total hours: ${(totalMinutes / 60).toFixed(2)}</p>
  <table>
    <thead>
      <tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr>
    </thead>
    <tbody>
      ${rows
        .map(
          (row) =>
            `<tr>${headers
              .map((h) => `<td>${escapeHtml(String(row[h as keyof typeof row] ?? ""))}</td>`)
              .join("")}</tr>`,
        )
        .join("")}
    </tbody>
  </table>
</body>
</html>`;
  downloadBlob(
    new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" }),
    `royal-heights-timesheets-${todayKey()}.xls`,
  );
}

function exportTimesheetsPdf(entries: WorkOrderTimesheetRow[]) {
  const pdf = makeTimesheetPdf(entries);
  const bytes = Uint8Array.from(pdf, (ch) => ch.charCodeAt(0));
  downloadBlob(
    new Blob([bytes], { type: "application/pdf" }),
    `royal-heights-timesheets-${todayKey()}.pdf`,
  );
}

function makeTimesheetPdf(entries: WorkOrderTimesheetRow[]): string {
  const totalMinutes = entries.reduce((sum, e) => sum + e.minutes, 0);
  const pages: string[][] = [[]];
  const addLine = (line: string) => {
    const current = pages[pages.length - 1];
    if (current.length >= 54) pages.push([]);
    pages[pages.length - 1].push(toPdfText(line));
  };

  addLine("Royal Heights Work Order Timesheets");
  addLine(`Generated ${new Date().toLocaleString("en-US")}`);
  addLine(`Total entries: ${entries.length}    Total hours: ${(totalMinutes / 60).toFixed(2)}`);
  addLine("");
  for (const e of entries) {
    const firstLine = `${e.userName} | ${formatWoId(e.workOrderId)} | ${e.minutes} min`;
    const secondLine = `${e.workOrderTitle} | ${e.buildingName}`;
    const thirdLine = `${formatDateTime(e.startedAt)} to ${formatDateTime(e.stoppedAt)}`;
    for (const line of [firstLine, secondLine, thirdLine]) {
      for (const wrapped of wrapPdfLine(line, 92)) addLine(wrapped);
    }
    addLine("");
  }

  const objects: string[] = [];
  const pageObjectIds: number[] = [];
  objects.push("1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj");
  objects.push("2 0 obj << /Type /Pages /Kids [] /Count 0 >> endobj");
  objects.push("3 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Courier >> endobj");

  pages.forEach((lines, index) => {
    const pageId = 4 + index * 2;
    const contentId = pageId + 1;
    pageObjectIds.push(pageId);
    const content = [
      "BT /F1 9 Tf 42 750 Td",
      ...lines.map((line, lineIndex) => `${lineIndex === 0 ? "" : "0 -13 Td "}(${pdfEscape(line)}) Tj`),
      "ET",
    ].join(" ");
    objects.push(
      `${pageId} 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 3 0 R >> >> /Contents ${contentId} 0 R >> endobj`,
    );
    objects.push(`${contentId} 0 obj << /Length ${content.length} >> stream\n${content}\nendstream endobj`);
  });

  objects[1] = `2 0 obj << /Type /Pages /Kids [${pageObjectIds
    .map((id) => `${id} 0 R`)
    .join(" ")}] /Count ${pageObjectIds.length} >> endobj`;

  let pdf = "%PDF-1.4\n";
  const offsets: number[] = [0];
  for (const obj of objects) {
    offsets.push(pdf.length);
    pdf += `${obj}\n`;
  }
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets.slice(1)) {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return pdf;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function toPdfText(value: string): string {
  return value.replace(/[^\x20-\x7E]/g, "-");
}

function pdfEscape(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function wrapPdfLine(value: string, maxLength: number): string[] {
  if (value.length <= maxLength) return [value];
  const words = value.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (!current) {
      current = word;
    } else if (`${current} ${word}`.length <= maxLength) {
      current += ` ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}
